import { createColumnHelper } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import { datumKort } from "@/i18n/datum";
import {
  VOORKEUR,
  type Inschrijving,
  type PloegInschrijving,
} from "./useInschrijving";

/**
 * The ploegen this korps has entered, one row each. Picking a row opens the ploeg - the row is
 * the control, which is what `DataTable` is built for, so no cell holds a button of its own.
 */
export function PloegenTabel({
  inschrijving,
  korpsId,
  onKies,
}: {
  inschrijving: Inschrijving;
  /** Without it, the table shows no export link. A test that renders the table alone leaves it out. */
  korpsId?: string;
  onKies: (ploegId: string) => void;
}) {
  const { t } = useTranslation("inschrijving");

  const helper = createColumnHelper<
    typeof dataTableFeatures,
    PloegInschrijving
  >();

  const columns = helper.columns([
    helper.accessor(
      (ploeg) => `${ploeg.name} ${klassenVan(inschrijving, ploeg)}`,
      {
        id: "ploeg",
        header: t("lijst.ploeg"),
        cell: (info) => (
          <span>
            <span className="font-medium">{info.row.original.name}</span>
            <span className="block text-xs text-muted-foreground">
              {klassenVan(inschrijving, info.row.original) ||
                t("lijst.geenKlasse")}
            </span>
            {info.row.original.promoties.map((bestemming) => (
              <span
                key={bestemming}
                className="block text-xs font-medium text-brand-blue"
              >
                {t("lijst.gepromoveerd", { bestemming })}
              </span>
            ))}
          </span>
        ),
      },
    ),
    helper.accessor((ploeg) => voorkeurenVan(t, inschrijving, ploeg), {
      id: "voorkeuren",
      header: t("lijst.voorkeursdatums"),
      cell: (info) => (
        <span className="text-sm text-muted-foreground">
          {info.getValue() || t("lijst.geenVoorkeuren")}
        </span>
      ),
    }),
  ]);

  return (
    <DataTable
      label={t("lijst.label")}
      data={inschrijving.ploegen}
      columns={columns}
      getRowId={(ploeg) => ploeg.ploegId}
      onSelect={onKies}
      empty={t("lijst.leeg")}
      exportHref={
        korpsId &&
        exportHref(
          "/api/inschrijfperiodes/{inschrijfperiodeId}/korpsen/{korpsId}/inschrijving/export",
          {
            path: {
              inschrijfperiodeId: inschrijving.inschrijfperiodeId,
              korpsId,
            },
          },
        )
      }
    />
  );
}

function klassenVan(inschrijving: Inschrijving, ploeg: PloegInschrijving) {
  return inschrijving.klassen
    .filter((klasse) => ploeg.klasseIds.includes(klasse.id))
    .map((klasse) => klasse.name)
    .join(", ");
}

/**
 * The voorkeuren in one line, in rank order, with the "geen voorkeur" marked. Short enough for a
 * cell: at most two dates and one exclusion.
 */
function voorkeurenVan(
  t: TFunction<"inschrijving">,
  inschrijving: Inschrijving,
  ploeg: PloegInschrijving,
) {
  const naam = (wedstrijddagId: string) => {
    const dag = inschrijving.wedstrijddagen.find(
      (d) => d.id === wedstrijddagId,
    );
    return dag ? datumKort(dag.date) : t("lijst.onbekendeDag");
  };

  return [...ploeg.voorkeuren]
    .sort((a, b) => a.kind - b.kind || Number(a.rank) - Number(b.rank))
    .map((voorkeur) =>
      voorkeur.kind === VOORKEUR.geenVoorkeur
        ? t("lijst.geenVoorkeurKort", { datum: naam(voorkeur.wedstrijddagId) })
        : t("lijst.voorkeurKort", {
            rang: Number(voorkeur.rank),
            datum: naam(voorkeur.wedstrijddagId),
          }),
    )
    .join(" · ");
}

/** Its own columns, because the real ones close over the inschrijving that is still missing. */
const skeletonKolommen = (t: TFunction<"inschrijving">) =>
  createColumnHelper<typeof dataTableFeatures, PloegInschrijving>().columns([
    { id: "ploeg", header: t("lijst.ploeg"), accessorFn: () => "" },
    {
      id: "voorkeuren",
      header: t("lijst.voorkeursdatums"),
      accessorFn: () => "",
    },
  ]);

export function PloegenTabelSkeleton() {
  const { t } = useTranslation("inschrijving");

  return (
    <DataTableSkeleton
      label={t("lijst.label")}
      columns={skeletonKolommen(t)}
      rows={3}
      twoLine={["ploeg"]}
    />
  );
}
